
import React, { useState, useEffect, useMemo } from "react";
import { useLeads, Lead } from "@/context/LeadContext";
import { usePartners } from "@/context/PartnerContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/components/ui/use-toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { phpApi } from "@/integrations/php/client";
import { useNavigate } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { el } from "date-fns/locale";
import {
  MessageSquare,
  Mail,
  Users,
  Send,
  AlertCircle,
  Clock3,
  RefreshCw,
  Search,
  CheckCircle2,
} from "lucide-react";
import { PageHeader } from "@/components/layouts/PageHeader";
import { StatCard } from "@/components/ui/StatCard";
import UnassignedLeadNotifier from "@/components/messages/UnassignedLeadNotifier";

interface SmsLog {
  id: string;
  sms_id: string;
  status: string;
  partner_id?: string;
  lead_id?: string;
  timestamp: string;
  created_at?: string;
}

interface EmailLog {
  id: string;
  tracking_id?: string;
  lead_id?: string;
  partner_id?: string;
  email?: string;
  event?: string;
  timestamp: string;
  created_at?: string;
}

const Messages = () => {
  const { leads, refreshLeads } = useLeads();
  const { partners } = usePartners();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [activeTab, setActiveTab] = useState("unassigned");
  const [refreshing, setRefreshing] = useState(false);

  // SMS logs state
  const [smsLogs, setSmsLogs] = useState<SmsLog[]>([]);
  const [smsLoading, setSmsLoading] = useState(false);
  const [smsSearch, setSmsSearch] = useState("");
  const [smsStatusFilter, setSmsStatusFilter] = useState("all");
  const [smsPartnerFilter, setSmsPartnerFilter] = useState("all");

  // Email logs state
  const [emailLogs, setEmailLogs] = useState<EmailLog[]>([]);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailSearch, setEmailSearch] = useState("");
  const [emailEventFilter, setEmailEventFilter] = useState("all");

  const unassignedCount = leads.filter((l) => !l.partnerId).length;

  const today = new Date().toISOString().slice(0, 10);
  const smsTodayCount = smsLogs.filter((s) =>
    (s.created_at || s.timestamp || "").startsWith(today)
  ).length;
  const emailOpenTodayCount = emailLogs.filter(
    (e) => e.event === "open" && (e.timestamp || e.created_at || "").startsWith(today)
  ).length;
  const smsFailedCount = smsLogs.filter((s) => s.status === "failed").length;

  const followUpLeads = useMemo(() => {
    return leads
      .filter(
        (l) =>
          (l.smsStatus === "sent" || l.smsStatus === "delivered") && !l.viewed
      )
      .map((l) => ({
        lead: l,
        hours: Math.floor(
          (Date.now() - new Date(l.updatedAt).getTime()) / 3_600_000
        ),
      }))
      .filter(({ hours }) => hours >= 12)
      .sort((a, b) => b.hours - a.hours)
      .slice(0, 30);
  }, [leads]);

  const fetchSmsLogs = async () => {
    setSmsLoading(true);
    try {
      const { data } = await phpApi
        .from("sms_delivery_status")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(300);
      if (data) setSmsLogs(data as SmsLog[]);
    } finally {
      setSmsLoading(false);
    }
  };

  const fetchEmailLogs = async () => {
    setEmailLoading(true);
    try {
      const { data } = await phpApi
        .from("email_logs")
        .select("*")
        .order("timestamp", { ascending: false })
        .limit(300);
      if (data) setEmailLogs(data as EmailLog[]);
    } finally {
      setEmailLoading(false);
    }
  };

  useEffect(() => {
    fetchSmsLogs();
    fetchEmailLogs();
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([refreshLeads(), fetchSmsLogs(), fetchEmailLogs()]);
    setRefreshing(false);
    toast({ title: "Ανανέωση", description: "Τα δεδομένα ενημερώθηκαν." });
  };

  const leadName = (id?: string) =>
    leads.find((l) => l.id === id)?.name ?? "—";
  const partnerName = (id?: string) =>
    partners.find((p) => p.id === id)?.name ?? "—";

  const filteredSms = useMemo(() => {
    return smsLogs.filter((s) => {
      if (smsStatusFilter !== "all" && s.status !== smsStatusFilter)
        return false;
      if (smsPartnerFilter !== "all" && s.partner_id !== smsPartnerFilter)
        return false;
      if (smsSearch.trim()) {
        const q = smsSearch.toLowerCase();
        if (
          !leadName(s.lead_id).toLowerCase().includes(q) &&
          !partnerName(s.partner_id).toLowerCase().includes(q) &&
          !(s.sms_id || "").toLowerCase().includes(q)
        )
          return false;
      }
      return true;
    });
  }, [smsLogs, smsStatusFilter, smsPartnerFilter, smsSearch, leads, partners]);

  const filteredEmails = useMemo(() => {
    return emailLogs.filter((e) => {
      if (emailEventFilter !== "all" && e.event !== emailEventFilter)
        return false;
      if (emailSearch.trim()) {
        const q = emailSearch.toLowerCase();
        if (
          !leadName(e.lead_id).toLowerCase().includes(q) &&
          !partnerName(e.partner_id).toLowerCase().includes(q) &&
          !(e.email || "").toLowerCase().includes(q)
        )
          return false;
      }
      return true;
    });
  }, [emailLogs, emailEventFilter, emailSearch, leads, partners]);

  const smsStatusBadge = (status: string) => {
    if (status === "sent")
      return (
        <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
          Εστάλη
        </Badge>
      );
    if (status === "delivered")
      return (
        <Badge className="bg-blue-100 text-blue-700 border-blue-200">
          Παραδόθηκε
        </Badge>
      );
    if (status === "failed") return <Badge variant="destructive">Αποτυχία</Badge>;
    return <Badge variant="outline">{status}</Badge>;
  };

  const emailEventBadge = (event?: string) => {
    if (event === "sent")
      return (
        <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
          Εστάλη
        </Badge>
      );
    if (event === "open")
      return (
        <Badge className="bg-blue-100 text-blue-700 border-blue-200">
          Άνοιγμα
        </Badge>
      );
    if (event === "click")
      return (
        <Badge className="bg-violet-100 text-violet-700 border-violet-200">
          Click
        </Badge>
      );
    if (event === "bounce") return <Badge variant="destructive">Bounce</Badge>;
    return <Badge variant="outline">{event || "—"}</Badge>;
  };

  const safeDate = (d?: string) => {
    if (!d) return null;
    const parsed = new Date(d);
    return isNaN(parsed.getTime()) ? null : parsed;
  };

  return (
    <div className="space-y-8 animate-fade-in">
      <PageHeader
        title="Μηνύματα"
        description="SMS, Email και ειδοποιήσεις"
        icon={<MessageSquare />}
        iconGradient="blue"
        actions={
          <Button
            onClick={handleRefresh}
            variant="outline"
            disabled={refreshing}
            className="border-slate-200"
          >
            <RefreshCw
              className={`h-4 w-4 mr-2 ${refreshing ? "animate-spin" : ""}`}
            />
            Ανανέωση
          </Button>
        }
      />

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          value={unassignedCount}
          title="Μη Ανατεθειμένα"
          icon={<Users />}
          variant="gradient"
          gradientColors="from-amber-500 to-orange-600"
          size="compact"
        />
        <StatCard
          value={smsTodayCount}
          title="SMS Σήμερα"
          icon={<Send />}
          variant="success"
          size="compact"
        />
        <StatCard
          value={emailOpenTodayCount}
          title="Email Ανοίγματα"
          icon={<Mail />}
          variant="info"
          size="compact"
        />
        <StatCard
          value={smsFailedCount}
          title="SMS Αποτυχίες"
          icon={<AlertCircle />}
          variant="gradient"
          gradientColors="from-rose-500 to-red-600"
          size="compact"
        />
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-slate-100 p-1 rounded-lg flex-wrap h-auto gap-1">
          <TabsTrigger
            value="unassigned"
            className="rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm"
          >
            <Users className="h-4 w-4 mr-2" />
            Μη Ανατεθειμένα
            {unassignedCount > 0 && (
              <Badge
                variant="secondary"
                className="ml-2 bg-amber-100 text-amber-700"
              >
                {unassignedCount}
              </Badge>
            )}
          </TabsTrigger>

          <TabsTrigger
            value="sms"
            className="rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm"
          >
            <MessageSquare className="h-4 w-4 mr-2" />
            SMS Αρχείο
            {smsFailedCount > 0 && (
              <Badge
                variant="secondary"
                className="ml-2 bg-rose-100 text-rose-700"
              >
                {smsFailedCount}
              </Badge>
            )}
          </TabsTrigger>

          <TabsTrigger
            value="email"
            className="rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm"
          >
            <Mail className="h-4 w-4 mr-2" />
            Email Αρχείο
          </TabsTrigger>

          <TabsTrigger
            value="followup"
            className="rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm"
          >
            <Clock3 className="h-4 w-4 mr-2" />
            Follow-up
            {followUpLeads.length > 0 && (
              <Badge
                variant="secondary"
                className="ml-2 bg-rose-100 text-rose-700"
              >
                {followUpLeads.length}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* ── ΜΗ ΑΝΑΤΕΘΕΙΜΕΝΑ ── */}
        <TabsContent value="unassigned" className="mt-6">
          <UnassignedLeadNotifier
            leads={leads}
            onSuccess={() => refreshLeads()}
          />
        </TabsContent>

        {/* ── SMS ΑΡΧΕΙΟ ── */}
        <TabsContent value="sms" className="mt-6">
          <Card className="border-slate-200">
            <CardHeader className="pb-3 border-b border-slate-100">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    value={smsSearch}
                    onChange={(e) => setSmsSearch(e.target.value)}
                    placeholder="Αναζήτηση επαφής, συνεργάτη..."
                    className="pl-9 bg-slate-50 border-slate-200"
                  />
                </div>
                <Select
                  value={smsStatusFilter}
                  onValueChange={setSmsStatusFilter}
                >
                  <SelectTrigger className="w-44 bg-slate-50 border-slate-200">
                    <SelectValue placeholder="Κατάσταση" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Όλες</SelectItem>
                    <SelectItem value="sent">Εστάλη</SelectItem>
                    <SelectItem value="delivered">Παραδόθηκε</SelectItem>
                    <SelectItem value="failed">Αποτυχία</SelectItem>
                  </SelectContent>
                </Select>
                <Select
                  value={smsPartnerFilter}
                  onValueChange={setSmsPartnerFilter}
                >
                  <SelectTrigger className="w-52 bg-slate-50 border-slate-200">
                    <SelectValue placeholder="Συνεργάτης" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Όλοι οι συνεργάτες</SelectItem>
                    {partners.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent className="pt-4">
              {smsLoading ? (
                <p className="text-sm text-slate-400 py-10 text-center">
                  Φόρτωση...
                </p>
              ) : filteredSms.length === 0 ? (
                <p className="text-sm text-slate-400 py-10 text-center">
                  Δεν υπάρχουν εγγραφές.
                </p>
              ) : (
                <div className="space-y-2 max-h-[560px] overflow-y-auto pr-1">
                  {filteredSms.map((s) => {
                    const d = safeDate(s.created_at || s.timestamp);
                    return (
                      <div
                        key={s.id}
                        className="flex items-center justify-between p-3 rounded-xl border border-slate-100 hover:bg-slate-50 gap-3"
                      >
                        <div className="min-w-0">
                          <p className="font-medium text-slate-900 text-sm truncate">
                            {leadName(s.lead_id)}
                          </p>
                          <p className="text-xs text-slate-500">
                            {partnerName(s.partner_id)}
                          </p>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          {smsStatusBadge(s.status)}
                          <span className="text-xs text-slate-400 hidden sm:block">
                            {d
                              ? formatDistanceToNow(d, {
                                  locale: el,
                                  addSuffix: true,
                                })
                              : "—"}
                          </span>
                          {s.lead_id && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs"
                              onClick={() =>
                                navigate(`/admin/lead/${s.lead_id}`)
                              }
                            >
                              Άνοιγμα
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── EMAIL ΑΡΧΕΙΟ ── */}
        <TabsContent value="email" className="mt-6">
          <Card className="border-slate-200">
            <CardHeader className="pb-3 border-b border-slate-100">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    value={emailSearch}
                    onChange={(e) => setEmailSearch(e.target.value)}
                    placeholder="Αναζήτηση επαφής, email..."
                    className="pl-9 bg-slate-50 border-slate-200"
                  />
                </div>
                <Select
                  value={emailEventFilter}
                  onValueChange={setEmailEventFilter}
                >
                  <SelectTrigger className="w-44 bg-slate-50 border-slate-200">
                    <SelectValue placeholder="Γεγονός" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Όλα τα γεγονότα</SelectItem>
                    <SelectItem value="sent">Εστάλη</SelectItem>
                    <SelectItem value="open">Άνοιγμα</SelectItem>
                    <SelectItem value="click">Click</SelectItem>
                    <SelectItem value="bounce">Bounce</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent className="pt-4">
              {emailLoading ? (
                <p className="text-sm text-slate-400 py-10 text-center">
                  Φόρτωση...
                </p>
              ) : filteredEmails.length === 0 ? (
                <p className="text-sm text-slate-400 py-10 text-center">
                  Δεν υπάρχουν εγγραφές.
                </p>
              ) : (
                <div className="space-y-2 max-h-[560px] overflow-y-auto pr-1">
                  {filteredEmails.map((e) => {
                    const d = safeDate(e.timestamp || e.created_at);
                    return (
                      <div
                        key={e.id}
                        className="flex items-center justify-between p-3 rounded-xl border border-slate-100 hover:bg-slate-50 gap-3"
                      >
                        <div className="min-w-0">
                          <p className="font-medium text-slate-900 text-sm truncate">
                            {leadName(e.lead_id)}
                          </p>
                          <p className="text-xs text-slate-500">
                            {e.email || partnerName(e.partner_id)}
                          </p>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          {emailEventBadge(e.event)}
                          <span className="text-xs text-slate-400 hidden sm:block">
                            {d
                              ? formatDistanceToNow(d, {
                                  locale: el,
                                  addSuffix: true,
                                })
                              : "—"}
                          </span>
                          {e.lead_id && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs"
                              onClick={() =>
                                navigate(`/admin/lead/${e.lead_id}`)
                              }
                            >
                              Άνοιγμα
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── FOLLOW-UP ── */}
        <TabsContent value="followup" className="mt-6">
          {followUpLeads.length === 0 ? (
            <Card className="border-slate-200">
              <CardContent className="py-16 text-center">
                <CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto mb-3" />
                <p className="text-slate-700 font-medium">
                  Δεν υπάρχουν εκκρεμή follow-ups
                </p>
                <p className="text-sm text-slate-400 mt-1">
                  Όλα τα μηνύματα έχουν αναγνωστεί ή είναι πρόσφατα.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-slate-500">
                {followUpLeads.length} leads με SMS που δεν διαβάστηκαν ≥ 12
                ώρες
              </p>
              {followUpLeads.map(({ lead, hours }) => (
                <Card key={lead.id} className="border-amber-200 bg-amber-50/40">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between gap-4">
                      <div className="min-w-0">
                        <p className="font-medium text-slate-900">
                          {lead.name}
                        </p>
                        <p className="text-sm text-slate-500">
                          {lead.partnerName || "Χωρίς συνεργάτη"} •{" "}
                          {lead.phone}
                        </p>
                        <p className="text-xs text-slate-400 mt-0.5 truncate">
                          {lead.address}
                        </p>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <Badge
                          variant="outline"
                          className="border-amber-300 text-amber-700 bg-amber-100"
                        >
                          {hours}h
                        </Badge>
                        <Button
                          size="sm"
                          onClick={() => navigate(`/admin/lead/${lead.id}`)}
                        >
                          Άνοιγμα
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Messages;
